// import * as Result from 'postcss/lib/result';

import compStyle from './addTocart.css?inline';

import type { PropsWithChildren } from 'react';
import { dispatchErrorEvent, dispatchSuccessEvent } from './addTocart.events';
import { cn } from './helper';
// @root points to root of parent dir. Use it like this:
// import globalStyle from '@root/src/app/globals.css?inline'

// styles need to be exported to entrypoint like this:
const stylesList = [
  { id: 'CompStyles', style: compStyle },
  // { id: 'GlobalStyles', style: globalStyle },
];
const sapCommerTestUrl = import.meta.env.PROD
  ? 'https://api.cu91cv9jmr-stoberant1-p1-public.model-t.cc.commerce.ondemand.com/occ/v2'
  : 'https://api.cu91cv9jmr-stoberant1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2';

// this props also get exported to entrypoint
interface AddToCartPropsBase {
  items?: CartItem[];
  token?: string;
  store?: 'sat' | 'sdi';
  label?: string;
  disabled?: 'true' | 'false';
  lang?: string;
  classes?: string;
}

interface CartItem {
  quantity: number;
  stoeProductConfiguration: string;
}

type AddToCartProps = PropsWithChildren<AddToCartPropsBase>;

const BUTTON_CLASS = {
  default:
    'p-2 font-bold bg-white border-2 border-stoe-gray-dark text-stoe-gray-dark hover:bg-stoe-gray-dark hover:text-white',
  primary: 'p-2 font-bold bg-stoe-blue  text-white hover:bg-stoe-gray-dark hover:text-white',
  disabled:
    'p-2 font-bold bg-white border-2 pointer-events-none cursor-not-allowed border-stoe-gray-light bg-stoe-gray-light text-white',
};

const addToCartPost = async (
  token: string,
  locale: string,
  store: AddToCartProps['store'],
  items: CartItem[],
): Promise<any> => {
  const baseSite = `${store?.toLocaleLowerCase()}Site`;

  try {
    const response = await fetch(
      `${sapCommerTestUrl}/${baseSite}/users/current/carts/current/entries?fields=FULL&lang=${locale.substring(
        0,
        2,
      )}`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(
          items.map((item) => ({
            quantity: item.quantity,
            stoeProductConfiguration: item.stoeProductConfiguration,
          })),
        ),
      },
    );

    const result = await response.json();
    if (result.errors) throw result.errors;
    return result;
  } catch (error) {
    console.error('addToCart error', error);
    throw error;
  }
};
const createNewCart = async (
  token: string,
  locale: string,
  store: AddToCartProps['store'],
): Promise<boolean> => {
  const baseSite = `${store?.toLocaleLowerCase()}Site`;

  try {
    const respone = await fetch(
      `${sapCommerTestUrl}/${baseSite}/users/current/carts/?fields=FULL&lang=${locale.substring(
        0,
        2,
      )}`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    const result = await respone.json();
    if (result.errors) throw result.errors;
    return true;
  } catch (error) {
    console.error('createNewCart error', error);
    throw error;
  }
};

const getCart = async (
  token: string,
  locale: string,
  store: AddToCartProps['store'],
): Promise<boolean> => {
  const baseSite = `${store?.toLocaleLowerCase()}Site`;
  const cartUrl = `${sapCommerTestUrl}/${baseSite}/users/current/carts/current?fields=FULL&lang=${locale.substring(
    0,
    2,
  )}`;

  try {
    const response = await fetch(cartUrl, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
    const result = await response.json();

    if (result.errors) {
      const cartNotFound = result.errors.some(
        (error: Error & { reason: string }) => error.reason === 'notFound',
      );
      if (cartNotFound) {
        return false;
      }
      throw result.errors;
    }
    return true;
  } catch (error) {
    throw error;
  }
};

const postToCart = async (
  accessToken: string,
  locale: string,
  store: AddToCartProps['store'],
  items: CartItem[] | string,
) => {
  let parsedItems: CartItem[];
  if (typeof items === 'string') {
    try {
      parsedItems = JSON.parse(items);
    } catch (e) {
      console.error('Failed to parse items:', e);
      dispatchErrorEvent(new Error('Invalid items format'));
      return;
    }
  } else {
    parsedItems = items;
  }

  if (!Array.isArray(parsedItems)) {
    console.error('Items is not an array:', parsedItems);
    dispatchErrorEvent(new Error('Items must be an array'));
    return;
  }

  // Normalize items: ensure stoeProductConfiguration is always a string
  const normalizedItems = parsedItems.map((item) => ({
    quantity: item.quantity,
    stoeProductConfiguration:
      typeof item.stoeProductConfiguration === 'string'
        ? item.stoeProductConfiguration
        : JSON.stringify(item.stoeProductConfiguration),
  }));

  let hasActiveCart = false;

  try {
    if (!accessToken) throw Error('not authorized');
    if (!normalizedItems || normalizedItems.length === 0) throw Error('no items to add');

    const hasCart = await getCart(accessToken, locale, store);
    if (hasCart) hasActiveCart = true;
    if (!hasActiveCart) {
      const successfulCartCreation = await createNewCart(accessToken, locale, store);
      if (successfulCartCreation) hasActiveCart = true;
    }

    if (hasActiveCart) {
      const cartResponse = await addToCartPost(accessToken, locale, store, normalizedItems);
      if (cartResponse) {
        dispatchSuccessEvent(cartResponse);
      }
    }
  } catch (error) {
    console.error('error', error);
    dispatchErrorEvent(error as Error);
  }
};

const AddToCart: React.FC<AddToCartProps> = ({
  items,
  token,
  store = 'sat',
  label = 'Add to cart',
  disabled = 'false',
  lang = 'de-DE',
  classes,
}: PropsWithChildren<AddToCartProps>) => {
  const handleClick = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    if (token && lang && store && items) {
      postToCart(token, lang, store, items);
    }
  };
  return (
    <>
      <button
        className={cn(BUTTON_CLASS.primary, classes, 'w-full font-cb-bold')}
        data-testid="stoe-addTocart"
        disabled={disabled === 'true' || !token}
        onClick={handleClick}
      >
        {label}
      </button>
    </>
  );
};

export { stylesList };
export type { AddToCartProps };
export default AddToCart;
